import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useGoogleConnection, useToast } from '@/store/appStore'
import { resolveCurrentUid } from '@/utils/auth'
import { requestUserBankAccounts } from '@/services/userData'
import type { BankAccount } from '@/services/userData'
import { stagePendingBankAccounts } from '@/v1/context/onboardingDraftStorage'
import {
  removeOnboardingBankRepair,
  writeBankReconciliation,
  bankRepairGeneration,
  type BankReconciliationReturn,
} from '@/v1/utils/onboardingBankRepairStorage'
import { usePlaidLink } from 'react-plaid-link'
import {
  addPlaidItem,
  relinkPlaidItem,
  clearPlaidLinkSession,
  completePlaidUpdate,
  exchangePlaidPublicToken,
  getPlaidLinkSession,
} from '@/services/plaid'
import { clearCreateSheetRepairHint } from '@/v1/pages/app/createSheetFlowStorage'
import {
  isOnboardingGoogleReturn,
  onboardingGoogleNonce,
  readOnboardingGoogleSession,
  V1_GOOGLE_RETURN_PATH,
} from '@/v1/utils/googleOAuthHistory'
import { completeOnboardingGoogle } from '@/v1/utils/completeOnboardingGoogle'
import { GENERIC_PLAID_OAUTH_ERROR, plaidOAuthErrorMessage } from './plaidOAuthError'
import { completeGoogleReconnect } from '@/v1/utils/completeGoogleReconnect'
import {
  GOOGLE_CREATE_RETURN,
  readGoogleReconnectSession,
  nonceFromGoogleReturn,
} from '@/v1/utils/googleReconnectSession'

type NavigationApi = {
  entries(): { key: string }[]
  traverseTo(key: string): { finished: Promise<unknown> }
}

/** Where a V1 onboarding session continues after Link finishes. */
const V1_AFTER_CONNECT = '/v1/onboarding/select-card'

const PlaidOAuthCallback = () => {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [status, setStatus] = useState<'loading' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState<string>()
  const [session] = useState(getPlaidLinkSession)

  const { open, ready } = usePlaidLink({
    token: session?.linkToken ?? null,
    receivedRedirectUri: window.location.href,
    onSuccess: async (publicToken) => {
      if (!session) return
      // V0 and V1 share this callback. A V1 session needs its accounts carried
      // across the redirect either way, but only an onboarding one continues to
      // the card step — sending a dashboard user there would bounce them right
      // back out, since that route is closed to anyone who has finished.
      // The token is exchanged as whoever Firebase authenticates as, which is not
      // necessarily who opened Link: getIdToken waits on its own listener, so it
      // can resolve a different user after an account switch in another tab.
      //
      // Resolved the same way getIdToken resolves it, not read from the auth
      // context. The context is still null immediately after the redirect, which
      // is precisely when this runs — comparing against it skipped the check in
      // the common case and left the bank attachable to the wrong account.
      const currentUid = await resolveCurrentUid()
      if (session.uid && session.uid !== currentUid) {
        clearPlaidLinkSession()
        console.error('Plaid OAuth completed for a different signed-in user; not exchanging')
        setStatus('error')
        return
      }
      const ownerUid = session.uid ?? currentUid
      if (!ownerUid) {
        setStatus('error')
        return
      }
      const assertCallbackOwner = async () => {
        if ((await resolveCurrentUid()) !== ownerUid) {
          throw new Error(
            'Signed-in account changed. Return to the account that started this bank connection.'
          )
        }
      }

      const isV1Session = Boolean(session.returnPath?.startsWith('/v1/'))
      const isV1Onboarding = Boolean(session.returnPath?.startsWith('/v1/onboarding/'))
      let connectedAccounts: BankAccount[] = []
      // Onboarding verifies every updated bank against its saved selections after
      // OAuth, because the callback runs outside the mounted onboarding provider.
      let reconciliation: BankReconciliationReturn | null = null
      try {
        if (session.mode === 'update') {
          let repairTarget: BankReconciliationReturn | null = null
          if (isV1Onboarding) {
            const uid = session.uid ?? currentUid
            if (!uid || !session.itemId)
              throw new Error('The bank being repaired was not recorded.')
            // Validate before the update consumes removed accounts. A retry can
            // report no new removals, so we must already know which run owns them.
            repairTarget = {
              uid,
              generation: bankRepairGeneration(uid, session.onboardingGeneration),
              itemId: session.itemId,
              disconnected: false,
            }
          }
          // OAuth banks complete update mode here (not in the in-page flow), so the
          // removed-account notice must be surfaced on this path too.
          const result = await completePlaidUpdate(session.itemId, ownerUid)
          if (repairTarget) {
            reconciliation = repairTarget
            // Do not refresh or clear the hint here. The onboarding provider owns
            // draft reconciliation and empty-Item retirement, with bounded Retry.
            // Record the reconciliation before leaving, even when this is a retry
            // whose server response reports zero newly removed accounts.
            writeBankReconciliation(reconciliation.uid, reconciliation.generation, reconciliation)
          }
          // The update is already persisted server-side. Save its reconciliation
          // debt under the initiating user's run before an account switch can
          // abort this callback; retries may report zero newly removed accounts.
          await assertCallbackOwner()
          if (result.removedAccountCount > 0) {
            const accounts = `${result.removedAccountCount} account${
              result.removedAccountCount === 1 ? '' : 's'
            }`
            const prefs =
              result.removedSyncPreferenceCount > 0 ? ' Their auto-sync settings were removed.' : ''
            showToast(`${accounts} disconnected during account update.${prefs}`, 'info')
          }
        } else if (session.mode === 'relink') {
          if (!session.itemId) throw new Error('The bank being relinked was not recorded')
          const relinked = await relinkPlaidItem(
            publicToken,
            session.itemId,
            session.enableRefresh,
            ownerUid
          )
          // Before the owner check, like the update debt above: a successful
          // relink has already retired the old Item, so a hint still naming it
          // sends the user at a relink whose previousItemId no longer exists.
          // Keyed on the initiating owner, so it clears the run that owed it.
          if (isV1Onboarding) removeOnboardingBankRepair(ownerUid, session.itemId)
          await assertCallbackOwner()
          // Same ordering as the in-page path: the deliberate keep is the more
          // specific outcome and must not be reported as a failed cleanup.
          if (relinked.oldItemKeptReason === 'institution_unverified') {
            // Same wording as the in-page path, and for the same reason: it must
            // not claim a reconnect it could not verify.
            showToast(
              'The selected bank was connected, but we could not verify it matches the bank you were repairing, so your original connection was kept. Check Bank & Accounts and disconnect whichever one you no longer need.',
              'info'
            )
          } else if (relinked.cleanupFailed) {
            showToast(
              'Your bank was reconnected, but the old connection could not be removed. Disconnect the old entry from Bank & Accounts.',
              'info'
            )
          } else if (relinked.pausedSyncPreferenceCount > 0) {
            showToast(
              'Your bank was reconnected. Automatic sync was paused for sheets using its old account IDs; reselect those accounts before turning sync back on.',
              'info'
            )
          }
        } else if (session.mode === 'additional') {
          // An OAuth bank finishes Link after a full page redirect, so the only
          // record that this was an added bank rather than a first one is the
          // stored session. Without this branch it falls through to the exchange
          // below, which replaces the user's connection — the bug this whole
          // path exists to avoid, reappearing for exactly the banks that take
          // the redirect route.
          const added = await addPlaidItem(publicToken, session.enableRefresh, undefined, ownerUid)
          if (isV1Session) {
            // Persist the consumed token's result under its initiating owner before
            // an owner switch can discard it. stageAllOnReturn replaces this
            // partial fallback with the full account list once that read succeeds.
            stagePendingBankAccounts(
              ownerUid,
              added.accounts,
              [],
              true,
              session.onboardingGeneration
            )
          }
          await assertCallbackOwner()
          // Every account, not just the one added, when the screen that started
          // this had banks still waiting to be walked. The in-page path merges
          // the two lists; here there is nothing to merge with, because this
          // renders outside the V1 provider after a full page reload. Reading
          // back after the Item is stored returns the same set — and staging only
          // `added` would drop a returning user's existing bank from the run
          // while leaving it connected at Plaid.
          if (session.stageAllOnReturn) {
            connectedAccounts = await requestUserBankAccounts(undefined, ownerUid)
            if (isV1Session)
              stagePendingBankAccounts(
                ownerUid,
                connectedAccounts,
                [],
                false,
                session.onboardingGeneration
              )
            await assertCallbackOwner()
          } else {
            connectedAccounts = added.accounts
          }
        } else {
          // The strict reading is whatever the session recorded at open time,
          // falling back to what the return path says. A session opened before
          // this field existed and finished after carries no flag, and defaulting
          // those to permissive would hand a returning V1 user the destructive
          // exchange for the length of the rollout. returnPath is old enough to
          // be on every such session, which is why isV1Session can stand in.
          // V0 still falls back to permissive: that is how it renews a lapsed
          // 25-minute session.
          await exchangePlaidPublicToken(
            publicToken,
            session.enableRefresh,
            session.requireNoExistingConnection ?? isV1Session,
            ownerUid
          )
          await assertCallbackOwner()
          // Only for a V1 session, which needs the accounts to show on the next
          // step. Everything else navigates back where it came from and reads
          // them itself, so this request would be wasted.
          //
          // The throwing reader, not the swallowing one. Through
          // fetchUserBankAccounts a transient failure comes back as [], which
          // would be staged as "this bank has no accounts" — the card step then
          // sends the user back to connect a bank that is already stored. Failing
          // here surfaces the error instead, and the bank stays connected.
          if (isV1Session) {
            connectedAccounts = await requestUserBankAccounts(undefined, ownerUid)
            await assertCallbackOwner()
          }
        }
        if (isV1Session && (session.mode === 'update' || session.mode === 'relink')) {
          clearCreateSheetRepairHint(ownerUid, session.itemId)
        }
        clearPlaidLinkSession()

        // The in-page path hands these to the onboarding context directly. This
        // one cannot — /oauth/callback renders outside the V1 provider — so the
        // accounts go into the persisted draft the provider restores on mount.
        // Without it the bank is connected and the card step has nothing to show.
        if (isV1Session) {
          if (isV1Onboarding) {
            if (reconciliation) {
              navigate('/v1/onboarding/select-template', {
                replace: true,
                state: { plaidBankReconciliation: reconciliation },
              })
              return
            }
            // The session's uid first: it was recorded when the flow started,
            // and the context may still be resolving after the redirect.
            //
            // New/relinked banks enter selection directly. Updates that lost
            // accounts use the persisted reconciliation path above instead.
            stagePendingBankAccounts(
              ownerUid,
              connectedAccounts,
              session.itemId &&
                !connectedAccounts.some((account) => account.itemId === session.itemId)
                ? [session.itemId]
                : [],
              false,
              session.onboardingGeneration
            )
            navigate(V1_AFTER_CONNECT, { replace: true })
            return
          }
          if (session.mode !== 'update') {
            // Never on an update, whatever it removed. Banks & Accounts consumes any
            // staged accounts as a bank that was just added and opens the naming
            // flow for it, so staging a repair's refreshed list walks the user
            // through naming a bank they already had. The page reloads here anyway
            // and reads the current list itself.
            stagePendingBankAccounts(
              ownerUid,
              connectedAccounts,
              [],
              false,
              session.onboardingGeneration
            )
          }
          // The dashboard returns to its original view. New connections use the
          // staged accounts; update sessions reload the current bank list there.
        }

        navigate(session.returnPath || '/v1', { replace: true })
      } catch (error) {
        console.error('Plaid OAuth completion failed:', error)
        setErrorMessage(plaidOAuthErrorMessage(error))
        setStatus('error')
      }
    },
    onExit: (error) => {
      if (error) console.error('Plaid OAuth resume failed:', error)
      // Through the same helper, which answers with the generic copy for a Link
      // exit — there is no backend response behind one. Said here rather than
      // left to a fallback in the markup, so the wording lives in one place.
      setErrorMessage(plaidOAuthErrorMessage(error))
      setStatus('error')
    },
  })

  useEffect(() => {
    if (!session) {
      setStatus('error')
      return
    }
    if (ready) open()
  }, [open, ready, session])

  return (
    <OAuthStatus
      status={status}
      loadingTitle="Reconnecting to Plaid…"
      errorMessage={errorMessage}
      onBack={() => navigate(session?.returnPath || '/v1', { replace: true })}
    />
  )
}

const OAuthStatus = ({
  status,
  loadingTitle,
  errorMessage,
  onBack,
}: {
  status: 'loading' | 'error'
  loadingTitle: string
  errorMessage?: string
  onBack: () => void
}) => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="bg-white border border-blue-400 rounded-2xl p-8 w-full max-w-md text-center shadow-md">
      {status === 'loading' ? (
        <>
          <p className="text-lg font-semibold text-gray-900 mb-2">{loadingTitle}</p>
          <p className="text-sm text-gray-500">Please wait while we finalize authentication.</p>
        </>
      ) : (
        <>
          <p className="text-lg font-semibold text-red-600 mb-2">Something went wrong</p>
          <p className="text-sm text-gray-500">{errorMessage ?? GENERIC_PLAID_OAUTH_ERROR}</p>
          <button onClick={onBack} className="mt-2 cursor-pointer text-blue-600">
            Go Back
          </button>
        </>
      )}
    </div>
  </div>
)

const GoogleOAuthCallback = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'loading' | 'error'>('loading')
  const [googleError, setGoogleError] = useState<string>()
  const { setGoogleConnected, setGoogleExpiry } = useGoogleConnection()
  const { showToast } = useToast()

  // Get redirect path from OAuth state param (passed through Google OAuth)
  const redirectPath = searchParams.get('state') || '/v1/app/dashboard'
  const returnFromGoogle = () => {
    const nonce = nonceFromGoogleReturn(redirectPath)
    const session = nonce ? readGoogleReconnectSession() : null
    const entry = session?.nonce === nonce ? session?.returnEntryKey : undefined
    const navigation = (window as Window & { navigation?: NavigationApi }).navigation
    if (entry && navigation?.entries().some((item) => item.key === entry)) {
      try {
        void navigation
          .traverseTo(entry)
          .finished.catch(() => navigate(redirectPath, { replace: true }))
        return
      } catch {
        // Browsers without a usable checkpoint return directly into the app.
      }
    }
    if (isOnboardingGoogleReturn(redirectPath)) {
      const session = readOnboardingGoogleSession()
      const returnEntryKey =
        session?.nonce === onboardingGoogleNonce(redirectPath) ? session?.returnEntryKey : undefined
      const navigation = (window as Window & { navigation?: NavigationApi }).navigation
      const entryStillExists =
        returnEntryKey && navigation?.entries().some((entry) => entry.key === returnEntryKey)

      if (returnEntryKey && navigation && entryStillExists) {
        try {
          // Traverse to the exact pre-OAuth app entry. Counting history entries
          // is not reliable across Google's account chooser and consent pages;
          // Chrome can add or replace those entries differently between runs,
          // which sent a successful authorization straight back to consent.
          void navigation.traverseTo(returnEntryKey).finished.catch(() => {
            navigate(V1_GOOGLE_RETURN_PATH, { replace: true })
          })
          return
        } catch {
          // Fall through to a direct app route below.
        }
      }
      navigate(V1_GOOGLE_RETURN_PATH, { replace: true })
      return
    }

    // Other OAuth callers, unsupported browsers and old tabs have no exact
    // entry checkpoint. Return directly to the app; never guess a history delta
    // that may land on a provider page.
    navigate(redirectPath, { replace: true })
  }

  useEffect(() => {
    const handleAuth = async () => {
      const code = searchParams.get('code')
      const denied = searchParams.get('error')

      // Even pre-deploy onboarding URLs without a nonce must fail closed rather
      // than falling through to V0's unbound exchange below.
      if (isOnboardingGoogleReturn(redirectPath)) {
        try {
          await completeOnboardingGoogle(redirectPath, code, denied)
          if (!denied) {
            setGoogleConnected(true)
            setGoogleExpiry(Date.now() + 3600000)
            showToast('Google Sheets connected', 'success')
          } else {
            showToast('Google access was not granted', 'info')
          }
          returnFromGoogle()
        } catch (error) {
          setGoogleError(
            error instanceof Error ? error.message : 'Google authorization did not finish.'
          )
          setStatus('error')
        }
        return
      }

      if (redirectPath.startsWith(`${GOOGLE_CREATE_RETURN}&googleReconnect=`)) {
        try {
          await completeGoogleReconnect(redirectPath, code, denied)
          if (!denied) {
            setGoogleConnected(true)
            setGoogleExpiry(Date.now() + 3600000)
          }
          returnFromGoogle()
        } catch (error) {
          setGoogleError(
            error instanceof Error
              ? error.message
              : 'Google authorization did not finish. Please try again.'
          )
          setStatus('error')
        }
        return
      }

      // Declining on Google's consent screen is an answer, not a failure. It
      // comes back here as error=access_denied with no code, which would
      // otherwise fall into the branch below and tell the user something went
      // wrong with a connection they chose not to make. Send them back to the
      // step they came from, which still offers the connect card.
      if (denied === 'access_denied') {
        showToast('Google access was not granted', 'info')
        returnFromGoogle()
        return
      }

      // Every connect start binds a nonce into `state`. Anything else is a crafted
      // link or a tab opened before nonces shipped; its code is never exchanged.
      console.error(denied ? `Google OAuth error: ${denied}` : 'Unbound Google OAuth return')
      setGoogleError(
        'This Google connection link has expired or was not started in this tab. Please reconnect Google Sheets.'
      )
      setStatus('error')
    }

    handleAuth()
    // this prevents infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <OAuthStatus
      status={status}
      loadingTitle="Connecting to Google Sheets…"
      errorMessage={googleError}
      onBack={returnFromGoogle}
    />
  )
}

const OAuthCallback = () => {
  const [searchParams] = useSearchParams()
  return searchParams.has('oauth_state_id') ? <PlaidOAuthCallback /> : <GoogleOAuthCallback />
}

export default OAuthCallback
